/**
 * CollectPhotoVectorTileController
 *
 * A renderless component that adds CollectItemPhoto vector tile layer to the global MapLibre map.
 * Displays photos attached to collect items using server-side MVT for performance.
 *
 * Source layer: 'collect_item_photo'
 * Tile endpoint: GET /api/geocollect/tiles/collect-item-photo/{z}/{x}/{y}.pbf
 */
import { memo, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { isMapLoadedDoneAtom, showCollectPhotoVectorTilesAtom } from '../stores/mapStore';
import CollectPhotoVectorTileUtils from '../utils/CollectPhotoVectorTileUtils';

// Legacy source/layer IDs from the old GeoJSON cluster controller (CollectedPhotosMapController)
const LEGACY_SOURCE_ID = 'collected-photos-source';
const LEGACY_LAYERS = ['collected-photos-clusters', 'collected-photos-cluster-count'];

const cleanupLegacyLayers = () => {
  const map = window.map;
  if (!map) return;
  LEGACY_LAYERS.forEach((id) => { if (map.getLayer(id)) map.removeLayer(id); });
  if (map.getSource(LEGACY_SOURCE_ID)) map.removeSource(LEGACY_SOURCE_ID);
};

const CollectPhotoVectorTileController = () => {
  const currentProject = useAtomValue(currentProjectAtom);
  const isMapLoadedDone = useAtomValue(isMapLoadedDoneAtom);
  const showCollectPhotoTiles = useAtomValue(showCollectPhotoVectorTilesAtom);

  // Mount / unmount / project change
  useEffect(() => {
    if (!isMapLoadedDone) return;

    // Remove any leftover layers from the old GeoJSON cluster approach
    cleanupLegacyLayers();

    CollectPhotoVectorTileUtils.addToMap(currentProject?.id);
    CollectPhotoVectorTileUtils.moveLayerToTop();

    return () => {
      CollectPhotoVectorTileUtils.removeFromMap();
    };
  }, [isMapLoadedDone, currentProject?.id]);

  // Toggle visibility
  useEffect(() => {
    if (!isMapLoadedDone) return;
    CollectPhotoVectorTileUtils.setVisible(showCollectPhotoTiles);
  }, [isMapLoadedDone, showCollectPhotoTiles]);

  // Re-add and move to top when basemap changes
  useEffect(() => {
    const handleBasemapChanged = () => {
      if (showCollectPhotoTiles) {
        CollectPhotoVectorTileUtils.addToMap(currentProject?.id);
        CollectPhotoVectorTileUtils.moveLayerToTop();
      }
    };

    window.addEventListener('basemapChanged', handleBasemapChanged);
    return () => {
      window.removeEventListener('basemapChanged', handleBasemapChanged);
    };
  }, [currentProject?.id, showCollectPhotoTiles]);

  return null;
};

export default memo(CollectPhotoVectorTileController);
